import { useQuery } from '@apollo/client';
import { Stack } from '@mui/material';
import { RuleGroupType } from '@procureai/query-builder';
import { Status, Widget, WidgetContent } from '@procureai/ui-mui';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { BaseDomain } from '~/common/types';
import { useConfig } from '~/components/ConfigProvider';
import { DataGridWidgetHeader, useDataGridStatsTranslation } from '~/components/DataGridCustom';
import { Map, MapMarkerVariant, MapProps } from '~/components/Map';
import { MapSourceId, MapViewState } from '~/components/Map/Map.types';
import { SupplierMapDetail } from '~/components/SupplierMapDetail/SupplierMapDetail';

import { LOCATION_GROUP_QUERY } from './LocationGroup.gql';
import { CircularProgressPaperStyled, WidgetContentInner } from './MapWidget.styled';
import { SUPPLIER_GEO_QUERY } from './SupplierGeo.gql';

const markerVariants: MapProps['markerVariants'] = {
  known: MapMarkerVariant.PRIMARY,
};

export interface MapWidgetProps {
  domain: BaseDomain;
  i18nPrefix: string;
  loading?: boolean;
  filter?: RuleGroupType;
  filterCount?: number;
  query: RuleGroupType | undefined;
}

interface RuleLocation {
  field: 'locationRadius';
  value: {
    lat: number;
    lng: number;
  };
}

export const MapWidget = ({ filterCount, filter, loading: loadingProp, query }: MapWidgetProps) => {
  const { t } = useTranslation();
  const { map, tenant } = useConfig();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [viewState, setViewState] = useState<MapViewState>({
    latitude: tenant.geo.latitude,
    longitude: tenant.geo.longitude,
    zoom: map.zoom,
  });

  useEffect(() => {
    const rules: RuleLocation[] = query
      ? query.rules.filter((rule): rule is RuleLocation => 'field' in rule && rule.field === 'locationRadius')
      : [];
    const location = rules.find((rule) => rule)?.value;

    if (location) {
      setViewState({
        latitude: location.lat,
        longitude: location.lng,
        zoom: map.zoom,
      });
    }
  }, [query, map]);

  const queryVariables = {
    filter,
    offset: 0,
    limit: 5000,
  };

  const { loading, data } = useQuery(SUPPLIER_GEO_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: queryVariables,
  });
  const { loading: locationGroupLoading, data: locationGroupData } = useQuery(LOCATION_GROUP_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: queryVariables,
  });

  const rowCount = data?.suppliersGeo?.features?.length || 0;
  const title = useDataGridStatsTranslation({ rowCount, filterCount });

  return (
    <Widget>
      <DataGridWidgetHeader
        title={title}
        center={
          <Stack
            direction="row"
            spacing={2}
            sx={{
              alignItems: 'center',
            }}
          >
            <Status statusColor="secondary.main" variant="caption">
              {t('supplierType.known')}
            </Status>
            <Status statusColor="text.primary" variant="caption">
              {t('supplierType.unknown')}
            </Status>
          </Stack>
        }
      />
      <WidgetContent disablePadding>
        <WidgetContentInner>
          <Map
            viewState={viewState}
            mapStyle={map.style}
            markerVariants={markerVariants}
            sources={[
              {
                data: data?.suppliersGeo as any,
                id: MapSourceId.supplierGeoData,
                cluster: true,
                clusterMaxZoom: 11,
                clusterRadius: 50,
                clusterMinPoints: 5,
              },
              { data: locationGroupData?.locationGroups as any, id: MapSourceId.locationGroupData },
            ]}
            loading={loading || locationGroupLoading || loadingProp}
            onSelect={setSelectedId}
            onViewStateSelect={setViewState}
          />
          {(loading || locationGroupLoading) && <CircularProgressPaperStyled elevation={9} label={t('Map.loading')} />}
          {selectedId ? (
            <SupplierMapDetail
              id={selectedId}
              onClose={() => {
                setSelectedId(null);
              }}
            />
          ) : null}
        </WidgetContentInner>
      </WidgetContent>
    </Widget>
  );
};
